import { fetchAuthSession } from 'aws-amplify/auth';
import { AwsSigner } from 'aws-sign-web';
import axios, { type InternalAxiosRequestConfig } from 'axios';
import qs from 'qs';
import config from 'src/config';
import { MIDWAY_TOKEN_HEADER, ID_TOKEN_HEADER } from 'src/constants';
import { isInternalUser } from 'src/utils/authProvider';
import { Logger } from 'src/utils/logger';
import { apiMetricsPublisher, METRIC } from 'src/utils/metrics';
import getMidwayToken, { redirectToMidwayLogin } from 'src/utils/midwayToken';

/**
 * Query serialization for the Leela API client and its SigV4 signer. Arrays use empty brackets
 * (`ids[]=a&ids[]=b`), not indices: aws-sign-web sorts query keys before encoding them while API
 * Gateway sorts after, so `ids[10]` and `ids[1]` swap places and any request with 11 or more array
 * entries fails with a signature mismatch. With one key per array, only the values are sorted, and
 * URL-safe values sort the same either way. Leela's Express query parser (qs) reads both formats; the
 * Bender and Fry clients keep the default format because their servers may not.
 */
export const QUERY_STRING_OPTIONS = { arrayFormat: 'brackets' } as const;

interface SwaggerFetchRequest {
  headers?: Record<string, string | Record<string, string> | unknown>;
  [k: string]: string | Record<string, string> | unknown;
}
export const apiClient = axios.create({
  baseURL: config.apiServerBaseUrl,
  paramsSerializer: params => qs.stringify(params, QUERY_STRING_OPTIONS),
});

export const benderApiClient = axios.create({
  baseURL: config.benderApiServerBaseUrl,
  paramsSerializer: params => qs.stringify(params),
});

export const fryApiClient = axios.create({
  baseURL: config.fryApiServerBaseUrl,
  paramsSerializer: params => qs.stringify(params),
});

const getCredentials = async () => {
  const { credentials } = await fetchAuthSession();
  if (!credentials) {
    return;
  }

  return {
    accessKeyId: credentials.accessKeyId,
    secretAccessKey: credentials.secretAccessKey,
    sessionToken: credentials.sessionToken,
  };
};

const getIdToken = async () => {
  const { tokens } = await fetchAuthSession();
  return tokens?.idToken;
};

const getMidwayTokenHeaders = async (): Promise<Record<string, string>> => {
  if (!(await isInternalUser())) {
    return {};
  }

  const midwayToken = await getMidwayToken();
  if (!midwayToken) {
    apiMetricsPublisher.publishCounter(METRIC.MidwayTokenFetchFailure, 1);
    redirectToMidwayLogin();
    throw new Error('Failed to retrieve Midway token; redirecting to login');
  }

  return { [MIDWAY_TOKEN_HEADER]: midwayToken };
};

const sigv4InterceptorWithIdToken = async (requestConfig: InternalAxiosRequestConfig) => {
  const queryString = requestConfig.params
    ? `?${qs.stringify(requestConfig.params, { ...QUERY_STRING_OPTIONS, encode: false })}`
    : '';

  const idToken = await getIdToken();
  const midwayTokenHeaders = await getMidwayTokenHeaders();

  const params = {
    method: requestConfig.method!,
    url: `${requestConfig.baseURL}${requestConfig.url!}${queryString}`,
    body: requestConfig.data ? JSON.stringify(requestConfig.data) : '',
  };

  const headers = await signRequest(params);
  requestConfig.headers = {
    ...requestConfig.headers,
    ...headers,
    [ID_TOKEN_HEADER]: idToken,
    ...midwayTokenHeaders,
  };

  return requestConfig;
};

const sigv4Interceptor = async (requestConfig: InternalAxiosRequestConfig) => {
  const queryString = requestConfig.params
    ? `?${qs.stringify(requestConfig.params, { encode: false })}`
    : '';

  const params = {
    method: requestConfig.method!,
    url: `${requestConfig.baseURL}${requestConfig.url!}${queryString}`,
    body: requestConfig.data ? JSON.stringify(requestConfig.data) : '',
  };

  const headers = await signRequest(params);
  requestConfig.headers = {
    ...requestConfig.headers,
    ...headers,
  };

  return requestConfig;
};

const sigV4HeadersFromSwaggerRequest = async (request: SwaggerFetchRequest) => {
  const { method, url, body } = request;
  return await signRequest({ method, url, body });
};

const signRequest = async (params: { method: unknown; url: unknown; body?: unknown }) => {
  const { method, url, body } = params;
  const credentials = await getCredentials();
  if (!credentials) {
    Logger.error('Unable to load credentials');
    apiMetricsPublisher.publishCounter(METRIC.Error, 1);

    return {};
  }
  const requestToSign = {
    method: method as string,
    url: url as string,
    body,
  };

  const signingConfig = {
    region: config.region,
    service: 'execute-api',
    ...credentials,
  };

  return new AwsSigner(signingConfig).sign(requestToSign);
};

export const swaggerRequestInterceptor = async (request: SwaggerFetchRequest): Promise<SwaggerFetchRequest> => {
  delete request.headers!.accept;
  const updatedRequest = { ...request };
  const updatedUrl = new URL(request.url as string);
  const baseUrl = new URL(config.apiServerBaseUrl);
  updatedUrl.protocol = baseUrl.protocol;
  updatedUrl.hostname = baseUrl.hostname;
  updatedUrl.port = baseUrl.port;

  const queryString = updatedUrl.search
    ? `?${qs.stringify(qs.parse(updatedUrl.search.slice(1)), { ...QUERY_STRING_OPTIONS, encode: false })}`
    : '';

  const url = `${updatedUrl.origin}${updatedUrl.pathname}${queryString}`;

  updatedRequest.url = url;

  const headers = await sigV4HeadersFromSwaggerRequest(updatedRequest);
  const idToken = await getIdToken();
  const midwayTokenHeaders = await getMidwayTokenHeaders();

  updatedRequest.headers = {
    ...updatedRequest.headers,
    ...headers,
    [ID_TOKEN_HEADER]: idToken,
    ...midwayTokenHeaders,
  };

  return updatedRequest;
};

apiClient.interceptors.request.use(sigv4InterceptorWithIdToken);
benderApiClient.interceptors.request.use(sigv4Interceptor);
fryApiClient.interceptors.request.use(sigv4Interceptor);
